import {
  Alert,
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as QueryKeys from "data";
import { useState } from "react";

import { useIdentity } from "contexts/identity-context";
import * as Routes from "routes";

import Card from "@mui/material/Card";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { ContentCopy } from "@mui/icons-material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { LoadingButton } from "@mui/lab";
import ConfirmationModal from "components/modals/confirmation-modal";
import BackButton from "components/navigation/back-button";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ViewEditInput } from "components/shared/view-edit-input";
import { ApiKey, ApiProduct, Status } from "data/models";
import { deleteApiKey, editApiKey } from "data/mutations";
import { fetchApiKeys, fetchApiProducts } from "data/queries";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Form, Formik } from "formik";
import { Gem, Info } from "lucide-react";
import { useHistory } from "react-router-dom";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";
import { updateApiKeyValidationSchema } from "./validation";

const keyStatus = [
  {
    value: "approved",
    label: "Approved",
    icon: <CheckCircleOutlineIcon color="success" />,
  },
  {
    value: "revoked",
    label: "Revoked",
    icon: <BlockIcon color="warning" />,
  },
];

export const EditApiKey = (props: any) => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const apiKeysQuery = useQuery(
    [QueryKeys.apiKeys, identityId],
    () => fetchApiKeys(),
    {
      onSuccess: (apps: ApiKey[]) => {
        const selected = apps.filter(
          (key: ApiKey) => key.name === props.match.params.keyName
        )[0];
        setSelectedKey(selected);
      },
    }
  );

  const getSelectedApp = (apps: ApiKey[]) => {
    if (selectedKey !== null) return selectedKey;
    return apps.filter(
      (key: ApiKey) => key.name === props.match.params.keyName
    )[0];
  };

  const apiProductsQuery = useQuery([QueryKeys.apiProducts], () =>
    fetchApiProducts()
  );

  const queryClient = useQueryClient();
  const apiKeyUpdateMutation = useMutation(
    (values: ApiKey) => {
      const newStatus: Status =
        values.status === "approved" ? "approve" : "revoke";
      return editApiKey({ ...values, status: newStatus });
    },
    {
      onSuccess: (updatedApiKey) => {
        const updatedApiKeys = apiKeysQuery.data?.map((apiKey: ApiKey) => {
          if (apiKey.name === updatedApiKey.name) {
            return updatedApiKey;
          }

          return apiKey;
        });
        queryClient.setQueryData(
          [QueryKeys.apiKeys, identityId],
          updatedApiKeys
        );
        setSelectedKey(updatedApiKey);
        setIsEditing(false);
      },
    }
  );

  const apiKeyDeleteMutation = useMutation(
    () => deleteApiKey(selectedKey?.name ?? ""),
    {
      onSuccess: (deletedApiKey) => {
        const remainingApiKeys = apiKeysQuery.data?.filter(
          (apiKey: ApiKey) => apiKey.name !== deletedApiKey.name
        );

        queryClient.setQueryData(
          [QueryKeys.apiKeys, identityId],
          remainingApiKeys
        );

        history.push(Routes.developerApiKeys);
      },
    }
  );
  const premiumPermissionKeys = [PermissionsKeys.premiumDataRead];
  const isDisabledPremiumData = (isPremium?: boolean) => {
    if (isPremium === undefined) {
      return false;
    } else {
      return isPremium && !isPermitted(premiumPermissionKeys);
    }
  };

  const isLoading: boolean =
    apiKeysQuery.isLoading || apiProductsQuery.isLoading;
  const isError: boolean =
    apiKeysQuery.isError ||
    apiProductsQuery.isError ||
    apiKeyUpdateMutation.isError;

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  // Extend Day.js with plugins
  dayjs.extend(utc);
  dayjs.extend(timezone);
  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "43px",
          position: "absolute",
          top: "-45px",
          right: "0px",
        }}
      >
        <BackButton />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CardHeader title="App Details" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            {isError && (
              <CardContent>
                <Alert variant="outlined" severity="error">
                  An error occurred while fetching the apps.
                </Alert>
              </CardContent>
            )}
            {isLoading && (
              <CardContent>
                <Skeleton variant="rectangular" height="200px" />
              </CardContent>
            )}
            {apiKeysQuery.isSuccess && apiProductsQuery.isSuccess && (
              <Formik<ApiKey>
                enableReinitialize={true}
                initialValues={{
                  createdDate: getSelectedApp(apiKeysQuery.data).createdDate,
                  expireDate: getSelectedApp(apiKeysQuery.data).expireDate,
                  consumerKey: getSelectedApp(apiKeysQuery.data).consumerKey,
                  consumerSecret: getSelectedApp(apiKeysQuery.data)
                    .consumerSecret,
                  name: getSelectedApp(apiKeysQuery.data).name,
                  status: getSelectedApp(apiKeysQuery.data).status,
                  products: getSelectedApp(apiKeysQuery.data).products,
                  notes: getSelectedApp(apiKeysQuery.data).notes,
                  displayName: getSelectedApp(apiKeysQuery.data).displayName,
                }}
                onSubmit={async (values) => {
                  apiKeyUpdateMutation.mutate(values);
                }}
                validationSchema={updateApiKeyValidationSchema}
              >
                {({
                  values: {
                    displayName,
                    status,
                    products,
                    consumerKey,
                    consumerSecret,
                    notes,
                    expireDate,
                    createdDate,
                  },
                  handleChange,
                  handleBlur,
                  handleReset,
                  isValid,
                  dirty,
                  resetForm,
                  errors,
                  touched,
                }) => {
                  return (
                    <Form>
                      <CardContent>
                        <Box
                          gap={1}
                          display="flex"
                          flexDirection="column"
                          marginBottom={isEditing ? 1 : 1}
                        >
                          <ViewEditInput isEditing={isEditing}>
                            <TextField
                              id="displayName"
                              name="displayName"
                              label="Name"
                              type="text"
                              margin="normal"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.displayName &&
                                Boolean(errors.displayName)
                              }
                              value={displayName}
                              variant="outlined"
                              fullWidth
                              size="small"
                              helperText={
                                touched.displayName && errors.displayName
                              }
                              inputProps={{ maxLength: 60 }}
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                              }}
                            />
                          </ViewEditInput>
                          <ViewEditInput
                            isEditing={isEditing}
                            label="App Status"
                            value={toTitleCase(status)}
                            startAdornment={
                              keyStatus.find((item) => item.value === status)
                                ?.icon
                            }
                          >
                            <FormControl fullWidth>
                              <InputLabel id="status-label">
                                App Status
                              </InputLabel>
                              <Select
                                id="status"
                                label="App Status"
                                labelId="status-label"
                                name="status"
                                size="small"
                                value={status}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={
                                  !isEditing || apiKeyUpdateMutation.isLoading
                                }
                                variant={"outlined"}
                                fullWidth
                              >
                                {keyStatus.map((option) => {
                                  return (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-start",
                                          gap: "10px",
                                        }}
                                      >
                                        {option.icon} {option.label}
                                      </div>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </ViewEditInput>
                          <Stack
                            direction="column"
                            sx={{
                              height: "40px",
                              marginLeft: "14px",
                              marginTop: isEditing ? 0 : "9px",
                            }}
                          >
                            <InputLabel
                              htmlFor="view-edit-value-consumer-key"
                              sx={{ fontSize: "12px" }}
                            >
                              Consumer Key
                            </InputLabel>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                id="view-edit-value-consumer-key"
                                variant="body1"
                              >
                                {consumerKey}
                              </Typography>
                              <IconButton
                                size="small"
                                sx={{ p: 0 }}
                                onClick={(): any => {
                                  navigator.clipboard.writeText(consumerKey);
                                }}
                              >
                                <ContentCopy />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Stack
                            direction="column"
                            sx={{
                              height: "40px",
                              marginLeft: "14px",
                            }}
                          >
                            <InputLabel
                              htmlFor="view-edit-value-consumer-secret"
                              sx={{ fontSize: "12px" }}
                            >
                              Consumer Secret
                            </InputLabel>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                id="view-edit-value-consumer-secret"
                                variant="body1"
                              >
                                {consumerSecret}
                              </Typography>
                              <IconButton
                                size="small"
                                sx={{ p: 0 }}
                                onClick={(): any => {
                                  navigator.clipboard.writeText(consumerSecret);
                                }}
                              >
                                <ContentCopy />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Stack
                            direction="column"
                            sx={{
                              height: "40px",
                              marginLeft: "14px",
                            }}
                          >
                            <InputLabel
                              htmlFor="view-edit-value-expiry"
                              sx={{ fontSize: "12px" }}
                            >
                              Expiry
                            </InputLabel>
                            <Typography
                              id="view-edit-value-expiry"
                              variant="body1"
                            >
                              {expireDate == -1 ? "Never" : expireDate}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="column"
                            sx={{
                              height: "40px",
                              marginLeft: "14px",
                            }}
                          >
                            <InputLabel
                              htmlFor="view-edit-value-created"
                              sx={{ fontSize: "12px" }}
                            >
                              Created
                            </InputLabel>
                            <Typography
                              id="view-edit-value-created"
                              variant="body1"
                            >
                              {dayjs(createdDate)
                                .tz("Australia/Sydney")
                                .format("D MMM, YYYY hh:mm:ss A z")}
                            </Typography>
                          </Stack>
                          <ViewEditInput
                            isEditing={isEditing}
                            label="Note"
                            value={notes}
                            style={{
                              height: "100%",
                              marginLeft: 2,
                            }}
                          >
                            <TextField
                              id="notes"
                              name="notes"
                              label="Note"
                              type="text"
                              size="small"
                              multiline
                              value={notes}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.notes && Boolean(errors.notes)}
                              helperText={touched.notes && errors.notes}
                              disabled={apiKeyUpdateMutation.isLoading}
                              variant="outlined"
                              fullWidth
                              sx={{
                                padding: "0px",
                                margin: "0px",
                                marginTop: 1,
                                marginBottom: "1px",
                              }}
                            />
                          </ViewEditInput>
                          <Stack
                            direction="column"
                            marginTop={isEditing ? 0 : "7px"}
                          >
                            <InputLabel
                              htmlFor="view-edit-value-created"
                              sx={{ fontSize: "16px" }}
                            >
                              API Products
                            </InputLabel>
                            <FormGroup>
                              <Grid
                                container
                                sx={{
                                  paddingLeft: "14px",
                                }}
                              >
                                {apiProductsQuery.data
                                  .sort((a: any, b: any) =>
                                    a.displayName.toUpperCase() >
                                    b.displayName.toUpperCase()
                                      ? 1
                                      : -1
                                  )
                                  .map((product: ApiProduct) => {
                                    return (
                                      <Grid item xs={12} md={6}>
                                        <Stack
                                          direction="row"
                                          spacing={0}
                                          alignItems="center"
                                        >
                                          <FormControlLabel
                                            sx={{
                                              marginRight: 1,
                                            }}
                                            control={
                                              <Checkbox
                                                id="products"
                                                name="products"
                                                onChange={handleChange}
                                                value={product.name}
                                                checked={products.includes(
                                                  product.name
                                                )}
                                                disabled={
                                                  !isEditing ||
                                                  isDisabledPremiumData(
                                                    product.premium &&
                                                      product.premium == "full"
                                                  )
                                                }
                                              />
                                            }
                                            label={product.displayName}
                                          />
                                          {product.premium &&
                                            product.premium == "full" && (
                                              <TooltipWrapper
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                                title={
                                                  isDisabledPremiumData(
                                                    (product.premium &&
                                                      product.premium ==
                                                        "full") ||
                                                      product.premium ==
                                                        "partial"
                                                  )
                                                    ? permissionDeniedMessageString(
                                                        premiumPermissionKeys
                                                      )
                                                    : "Premium Data"
                                                }
                                              >
                                                <Gem color="#1876d3" />
                                              </TooltipWrapper>
                                            )}
                                          {product.premium &&
                                            product.premium == "partial" && (
                                              <TooltipWrapper
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                                title={
                                                  isDisabledPremiumData(
                                                    product.premium &&
                                                      product.premium ==
                                                        "partial"
                                                  )
                                                    ? permissionDeniedMessageString(
                                                        premiumPermissionKeys,
                                                        {
                                                          "upgrade_required":
                                                            "Some functionality is limited to Team or higher",
                                                        }
                                                      )
                                                    : "Premium Data"
                                                }
                                              >
                                                <Info />
                                              </TooltipWrapper>
                                            )}
                                        </Stack>
                                      </Grid>
                                    );
                                  })}
                              </Grid>
                            </FormGroup>
                          </Stack>
                          {/* TODO: Un comment this when we are ready to go with new Team plan  */}
                          {/* <Stack direction="column" spacing={1}>
                            <Stack direction="row" spacing={1}>
                              <Info />
                              <Typography variant="caption">
                                Products have some functionality that is only
                                accessible when you have a Team Plan or higher
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Gem color="#1876d3" />
                              <Typography variant="caption">
                                Products are only accessible when you have a
                                Team Plan or higher
                              </Typography>
                            </Stack>
                          </Stack> */}
                        </Box>
                      </CardContent>
                      <CardActions
                        sx={{
                          justifyContent: "flex-end",
                        }}
                      >
                        {!isEditing && (
                          <Box
                            sx={{
                              justifyContent: "flex-end",
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,
                            }}
                          >
                            <TooltipWrapper
                              title={permissionDeniedMessageString([
                                PermissionsKeys.appsUpdate,
                              ])}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => setIsEditing(true)}
                                disabled={
                                  !isPermitted([PermissionsKeys.appsUpdate])
                                }
                              >
                                Edit
                              </Button>
                            </TooltipWrapper>
                            <TooltipWrapper
                              title={permissionDeniedMessageString([
                                PermissionsKeys.appsDelete,
                              ])}
                            >
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                onClick={() => {
                                  apiKeyDeleteMutation.reset();
                                  setDeleteModalIsOpen(true);
                                }}
                                disabled={
                                  !isPermitted([PermissionsKeys.appsDelete])
                                }
                              >
                                Delete
                              </Button>
                            </TooltipWrapper>
                          </Box>
                        )}
                        {isEditing && (
                          <Box
                            sx={{
                              justifyContent: "flex-end",
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,
                            }}
                          >
                            <Button
                              size="small"
                              variant="text"
                              disabled={apiKeyUpdateMutation.isLoading}
                              onClick={() => {
                                resetForm();
                                apiKeyUpdateMutation.reset();
                                setIsEditing(false);
                              }}
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              loading={apiKeyUpdateMutation.isLoading}
                              size="small"
                              variant="contained"
                              type="submit"
                              disabled={!isValid || !dirty}
                            >
                              Save
                            </LoadingButton>
                          </Box>
                        )}
                      </CardActions>
                      <ConfirmationModal
                        isLoading={apiKeyDeleteMutation.isLoading}
                        isError={apiKeyDeleteMutation.isError}
                        open={deleteModalIsOpen}
                        header={`Are you sure?`}
                        body={`Deleting "${selectedKey?.displayName}" API Key will permanently remove it from your account. This action cannot be undone.`}
                        leftButtonText={"Yes, Delete API Key"}
                        leftButtonColor="error"
                        rightButtonText={"No, Keep API Key"}
                        rightButtonColor="primary"
                        handleLeftButton={() => apiKeyDeleteMutation.mutate()}
                        handleRightButton={() => setDeleteModalIsOpen(false)}
                        handleClose={() => setDeleteModalIsOpen(false)}
                      />
                    </Form>
                  );
                }}
              </Formik>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
